import { Component, inject, Input, OnInit, Output } from '@angular/core';
import { MenuItem, PrimeTemplate } from 'primeng/api';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { AtlasConfiguration, Commune } from '../../../models/commune';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { GlobalsStore } from '../../../stores/globals.state';
import {
  DataContext,
  WidgetConfig,
} from '../../../services/grid-widget-config.service';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { HyperionContext } from '../../../models/base-entity';

@Component({
  selector: 'app-context-switch',
  standalone: true,
  imports: [PrimeTemplate, SelectButtonModule, FormsModule, TooltipModule],
  templateUrl: './context-switch.component.html',
  styleUrl: './context-switch.component.scss',
})
export class ContextSwitchComponent implements OnInit {
  readonly layerStore = inject(DataLayerStore);
  readonly globalStore = inject(GlobalsStore);
  readonly widgetStore = inject(WidgetStore);
  readonly activeUserStore = inject(ActiveUserStore);

  @Input() activeWidgetConfig!: WidgetConfig;

  activeCommune: Commune | null = null;
  contextOptions: MenuItem[] = [];
  atlasConfiguration?: AtlasConfiguration;
  canAccessExternalData = false;

  ngOnInit(): void {
    this.activeCommune = this.globalStore.activeCommune();
    this.atlasConfiguration = this.activeCommune?.atlasConfig;
    this.initializeContextOptions();
    this.checkExternalDataAccess();
  }

  checkExternalDataAccess() {
    const atlasConfig = this.activeCommune?.atlasConfig;
    const hyperionContext: HyperionContext = this.activeUserStore.context();

    if (atlasConfig) {
      if (
        !atlasConfig.allowWegweiserData ||
        atlasConfig.allowWegweiserData === 'all' ||
        (atlasConfig.allowWegweiserData === 'external' &&
          hyperionContext === 'atlasExternal') ||
        (atlasConfig.allowWegweiserData === 'internal' &&
          hyperionContext === 'atlasInternal')
      ) {
        this.canAccessExternalData = true;
      } else {
        this.canAccessExternalData = false;
      }
    } else {
      this.canAccessExternalData = true;
    }
  }

  /**
   * Initializes context options for the application. This sets up context-related settings
   * such as available options and default context based on the current state of layers and widgets.
   *
   * @return {void} This method does not return a value.
   */
  initializeContextOptions(): void {
    this.contextOptions = [
      {
        disabled: this.layerStore.asArray().length === 0,
        label: 'Kommunal-Atlas',
        tooltip:
          'Nutzen Sie Daten aus dem Kommunal-Atlas für Ihre Darstellung.',
        key: 'local',
      },
      {
        label: 'Wegweiser Kommune',
        tooltip:
          'Nutzen Sie Daten aus dem Wegweiser Kommune für Ihre Darstellung.',
        key: 'external',
      },
    ];
  }

  /**
   * Sets the context for the widget and updates the widget store properties accordingly.
   *
   * @param {DataContext} context - The context to be applied. Possible values are 'external' or other defined contexts.
   * @return {void} Does not return a value.
   */
  setContext(context: DataContext): void {
    this.widgetStore.setWidgetProperties({
      context,
      indicators: [],
      indicator: undefined,
      wegweiserData:
        context === 'external'
          ? {
              regions: [],
              indicators: [],
              years: [],
              geoData: null,
            }
          : null,
      wegweiserRequestParams: {
        regionIds: [],
        indicatorIds: [],
        years: [],
      },
    });
  }
}
